import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { 
  Plus, 
  Search, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Edit2, 
  Trash2, 
  X, 
  Save,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState({ name: '', contact: '', phone: '', email: '', address: '' });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSuppliers(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentSupplier.id) await api.put(`/suppliers/${currentSupplier.id}`, currentSupplier);
      else await api.post('/suppliers', currentSupplier);
      setIsModalOpen(false);
      setCurrentSupplier({ name: '', contact: '', phone: '', email: '', address: '' });
      loadSuppliers();
    } catch (err) {
      alert('Error saving supplier');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      setSuppliers(prev => prev.filter(s => s.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      alert('Error deleting supplier');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} suppliers?`)) return;
    const idsToDelete = Array.from(selectedIds);
    try {
      await api.post('/suppliers/bulk-delete', { ids: idsToDelete });
      setSuppliers(prev => prev.filter(s => !selectedIds.has(s.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert('Error deleting suppliers');
    }
  };

  const filtered = suppliers.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

  // Pagination Logic
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSuppliers = filtered.slice(indexOfFirstItem, indexOfLastItem);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentSuppliers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentSuppliers.map(s => s.id)));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Vendors</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Manage your vendor directory and contact information.</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button
            onClick={() => { setCurrentSupplier({ name: '', contact: '', phone: '', email: '', address: '' }); setIsModalOpen(true); }}
            className="bg-brand hover:bg-brand-light text-white px-6 py-2.5 rounded-xl font-black text-sm transition-all shadow-lg flex items-center gap-2 uppercase tracking-widest"
          >
            <Plus size={18} /> Add Vendor
          </button>
        </div>
      </div>

      <div className="bg-theme-card rounded-[2rem] shadow-soft border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme bg-theme-main/10">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={18} />
            <input
              type="text"
              placeholder="Search suppliers..."
              className="w-full pl-10 pr-4 py-2.5 bg-theme-main border border-theme rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main font-bold"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                {isAdmin && (
                  <th className="pl-6 py-4 w-10">
                    <button onClick={toggleSelectAll} className="text-brand">
                      {selectedIds.size === currentSuppliers.length && currentSuppliers.length > 0 ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                  </th>
                )}
                <th className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>Supplier Name</th>
                <th className="px-6 py-4">Contact Person</th>
                <th className="px-6 py-4">Phone / Email</th>
                <th className="px-6 py-4">Address</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentSuppliers.map(s => (
                <tr key={s.id} className={`hover:bg-theme-main/5 transition-colors group ${selectedIds.has(s.id) ? 'bg-brand/5' : ''}`}>
                  {isAdmin && (
                    <td className="pl-6 py-4">
                      <button onClick={() => toggleSelect(s.id)} className={selectedIds.has(s.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                        {selectedIds.has(s.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                      </button>
                    </td>
                  )}
                  <td className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center font-black border border-brand/20">
                        {s.name[0].toUpperCase()}
                      </div>
                      <span className="font-bold text-theme-main">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-theme-main font-medium">{s.contact || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-theme-main">{s.phone}</span>
                      <span className="text-[10px] text-theme-muted">{s.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-xs text-theme-muted max-w-xs truncate">{s.address || '-'}</td>
                  <td className="px-8 py-4 text-right">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setCurrentSupplier(s); setIsModalOpen(true); }}
                        className="p-2 text-theme-muted hover:text-brand hover:bg-brand/10 rounded-lg transition-all"
                      >
                        <Edit2 size={16} />
                      </button>
                      {isAdmin && (
                        <button 
                          onClick={() => handleDelete(s.id)}
                          className="p-2 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {currentSuppliers.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? "6" : "5"} className="px-6 py-12 text-center text-theme-muted italic">No suppliers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> vendors
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20">
                <ChevronLeft size={20} />
              </button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-start justify-center pt-24 z-50 animate-fade-in">
          <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-theme">
            <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-lg font-black text-theme-main uppercase tracking-tight">
                {currentSupplier.id ? 'Edit Supplier' : 'Add New Supplier'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-theme-muted hover:text-theme-main transition-colors"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Company Name</label>
                <input required type="text" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={currentSupplier.name} onChange={e => setCurrentSupplier({ ...currentSupplier, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Contact Person</label>
                  <input type="text" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={currentSupplier.contact} onChange={e => setCurrentSupplier({ ...currentSupplier, contact: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Phone</label>
                  <input type="text" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={currentSupplier.phone} onChange={e => setCurrentSupplier({ ...currentSupplier, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Email Address</label>
                <input type="email" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={currentSupplier.email} onChange={e => setCurrentSupplier({ ...currentSupplier, email: e.target.value })} />
              </div>
              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Office Address</label>
                <textarea rows="3" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main text-sm font-bold" value={currentSupplier.address} onChange={e => setCurrentSupplier({ ...currentSupplier, address: e.target.value })}></textarea>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 bg-theme-card border border-theme text-theme-muted rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-theme-main transition-colors">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-brand text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-brand/20 hover:bg-brand-light transition-all flex items-center justify-center gap-2">
                  <Save size={18} /> Save Vendor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
